import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaPhoneAlt, FaRegEnvelope } from "react-icons/fa";
import houses from "../pages/HouseInfo";

const FooterWrapper = styled.footer`
  background-color: #5e8b95;
  color: #fff;
  padding: 48px 40px 24px;
  width: 100%;
  box-sizing: border-box;
`;

const FooterGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.6fr 1fr 1.1fr 1fr;
  gap: 40px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 36px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const FooterSection = styled.div``;

const BrandName = styled.h2`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.4rem;
  margin: 0 0 8px;
  color: #fff;
`;

const BrandTagline = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.15rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 16px;
  line-height: 1.5;
`;

const SectionHeading = styled.h3`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 14px;
`;

const NavLink = styled(Link)`
  display: block;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.15rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  margin-bottom: 8px;
  transition: color 0.2s;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const PolicyItem = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.1rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 8px;
  line-height: 1.4;
`;

const ContactName = styled.p`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.2rem;
  font-weight: bold;
  color: #fff;
  margin: 0;
  line-height: 1.3;
`;

const ContactRole = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.85);
  margin: 2px 0 14px;
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1.2rem;
  font-weight: bold;
  color: #fff;
  text-decoration: none;
  margin-bottom: 10px;
  /* the email is long enough to overflow a narrow column */
  overflow-wrap: anywhere;
  transition: opacity 0.2s;

  svg {
    flex-shrink: 0;
    opacity: 0.85;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 1200px;
  margin: 32px auto 20px;
`;

const BottomRow = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: 1rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const Footer: React.FC = () => {
  const year = new Date().getFullYear();

  return (
    <FooterWrapper>
      <FooterGrid>
        <FooterSection>
          <BrandName>Cape Ocean Vista</BrandName>
          <BrandTagline>
            Vacation rentals on Cape Cod — oceanfront homes, beachfront condos,
            and waterfront retreats on Nantucket Sound.
          </BrandTagline>
          <NavLink to="/contact">Contact the owner</NavLink>
        </FooterSection>

        <FooterSection>
          <SectionHeading>Properties</SectionHeading>
          {houses.map((house) => (
            <NavLink key={house.propertyId} to={house.route}>
              {house.displayName}
            </NavLink>
          ))}
        </FooterSection>

        <FooterSection>
          <SectionHeading>Contact</SectionHeading>
          <ContactName>Yiping Ding</ContactName>
          <ContactRole>Property Manager</ContactRole>
          <ContactLink href="tel:+1-617-416-8883">
            <FaPhoneAlt size={14} aria-hidden="true" />
            1 (617) 416-8883
          </ContactLink>
          <ContactLink href="mailto:capeoceanvista@gmail.com">
            <FaRegEnvelope size={15} aria-hidden="true" />
            capeoceanvista@gmail.com
          </ContactLink>
        </FooterSection>

        <FooterSection>
          <SectionHeading>Policies</SectionHeading>
          <PolicyItem>Check-in after 4:00 pm</PolicyItem>
          <PolicyItem>Check-out before 11:00 am</PolicyItem>
          <PolicyItem>Pets welcome — max 2 dogs</PolicyItem>
          <PolicyItem>No smoking</PolicyItem>
        </FooterSection>
      </FooterGrid>

      <Divider />

      <BottomRow>
        <span>© {year} Cape Ocean Vista. All rights reserved.</span>
        <span>Owner: Yiping Ding</span>
      </BottomRow>
    </FooterWrapper>
  );
};

export default Footer;
